// ==================== NPC 控制器 ====================

import * as THREE from 'three';
import { NPCState, NPCConfig } from '../types/index.js';

export class NPCController {
  private npcs: Map<string, NPCState> = new Map();
  private scene: THREE.Scene;

  // 占位模型材质缓存
  private materialCache: Map<string, THREE.MeshStandardMaterial> = new Map();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** 创建占位 NPC 模型 */
  createPlaceholderModel(config: NPCConfig): THREE.Group {
    const group = new THREE.Group();
    const color = config.color || '#4488cc';

    // 身体 (圆柱)
    const bodyGeo = new THREE.CylinderGeometry(0.3, 0.35, 1.0, 12);
    const bodyMat = this.getMaterial(color);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = 0.5;
    group.add(body);

    // 头部 (球)
    const headGeo = new THREE.SphereGeometry(0.25, 16, 12);
    const headMat = this.getMaterial(this.lightenColor(color));
    const head = new THREE.Mesh(headGeo, headMat);
    head.position.y = 1.25;
    group.add(head);

    // 情绪指示器 (小方块，用于显示情绪)
    const emotionGeo = new THREE.BoxGeometry(0.15, 0.15, 0.15);
    const emotionMat = new THREE.MeshStandardMaterial({ color: 0x00ff00 });
    const emotionIndicator = new THREE.Mesh(emotionGeo, emotionMat);
    emotionIndicator.position.y = 1.6;
    emotionIndicator.name = 'emotionIndicator';
    group.add(emotionIndicator);

    // 对话气泡精灵 (隐藏状态)
    const bubbleSprite = this.createBubbleSprite('');
    bubbleSprite.name = 'bubble';
    bubbleSprite.visible = false;
    group.add(bubbleSprite);

    // 名字标签
    const labelSprite = this.createLabelSprite(config.name);
    labelSprite.name = 'label';
    labelSprite.position.y = 1.9;
    group.add(labelSprite);

    group.name = `npc_${config.id}`;
    group.userData = { npcId: config.id, config };

    return group;
  }

  /** 生成/更新 NPC */
  spawnNPC(config: NPCConfig, initialState?: Partial<NPCState>): NPCState {
    if (this.npcs.has(config.id)) {
      if (initialState) this.updateNPC(config.id, initialState);
      return this.npcs.get(config.id)!;
    }

    const mesh = this.createPlaceholderModel(config);
    mesh.position.set(...config.spawnPosition);
    if (config.spawnRotation) {
      mesh.rotation.y = THREE.MathUtils.degToRad(config.spawnRotation);
    }
    this.scene.add(mesh);

    const state: NPCState = {
      id: config.id,
      name: config.name,
      role: config.role,
      position: mesh.position.clone(),
      rotationY: mesh.rotation.y,
      currentAction: 'idle',
      currentEmotion: 'neutral',
      isTalking: false,
      mesh,
      label: mesh.getObjectByName('label') as THREE.Sprite | null,
    };

    this.npcs.set(config.id, state);
    return state;
  }

  /** 更新 NPC 状态 */
  updateNPC(id: string, updates: Partial<NPCState>): void {
    const state = this.npcs.get(id);
    if (!state || !state.mesh) return;

    if (updates.position) {
      state.mesh.position.copy(updates.position);
      state.position.copy(updates.position);
    }
    if (updates.rotationY !== undefined) {
      state.mesh.rotation.y = updates.rotationY;
      state.rotationY = updates.rotationY;
    }
    if (updates.currentEmotion) {
      this.setEmotionVisual(state, updates.currentEmotion);
      state.currentEmotion = updates.currentEmotion;
    }
    if (updates.isTalking !== undefined) {
      state.isTalking = updates.isTalking;
    }
    if (updates.currentAction) {
      state.currentAction = updates.currentAction;
    }
  }

  /** 显示对话气泡 */
  showDialogBubble(npcId: string, text: string, duration = 5000): void {
    const state = this.npcs.get(npcId);
    if (!state || !state.mesh) return;

    const bubble = state.mesh.getObjectByName('bubble') as THREE.Sprite;
    if (!bubble) return;

    // 更新气泡纹理
    const newBubble = this.createBubbleSprite(text);
    bubble.material = newBubble.material;
    bubble.scale.copy(newBubble.scale);
    bubble.visible = true;

    state.isTalking = true;

    setTimeout(() => {
      bubble.visible = false;
      state.isTalking = false;
    }, duration);
  }

  /** 设置情绪视觉 */
  private setEmotionVisual(state: NPCState, emotion: string): void {
    const indicator = state.mesh?.getObjectByName('emotionIndicator') as THREE.Mesh;
    if (!indicator) return;

    const colors: Record<string, number> = {
      neutral: 0x888888,
      happy: 0x00ff88,
      sad: 0x4488ff,
      angry: 0xff4444,
      surprised: 0xffaa00,
      thinking: 0xffaa00,
      confused: 0xaa44aa,
    };
    (indicator.material as THREE.MeshStandardMaterial).color.setHex(
      colors[emotion] || colors.neutral
    );
  }

  /** 创建对话气泡精灵 */
  private createBubbleSprite(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // 背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.beginPath();
    ctx.roundRect(10, 10, 492, 236, 20);
    ctx.fill();

    // 文字
    ctx.fillStyle = '#ffffff';
    ctx.font = '20px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    // 自动换行
    const maxWidth = 460;
    const lineHeight = 26;
    const words = text.split('');
    let line = '';
    let y = 24;
    for (const char of words) {
      const testLine = line + char;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line.length > 0) {
        ctx.fillText(line, 24, y);
        line = char;
        y += lineHeight;
        if (y > 220) break; // 超出高度
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, 24, y);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(3, 1.5, 1);
    sprite.position.y = 2.2;
    return sprite;
  }

  /** 创建名字标签精灵 */
  private createLabelSprite(name: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.beginPath();
    ctx.roundRect(4, 4, 248, 56, 10);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 128, 32);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(1.5, 0.375, 1);
    return sprite;
  }

  /** 获取材质（带缓存） */
  private getMaterial(color: string): THREE.MeshStandardMaterial {
    if (!this.materialCache.has(color)) {
      this.materialCache.set(
        color,
        new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.7 })
      );
    }
    return this.materialCache.get(color)!;
  }

  private lightenColor(hex: string): string {
    const c = new THREE.Color(hex);
    c.offsetHSL(0, 0.1, 0.2);
    return '#' + c.getHexString();
  }

  /** 获取所有 NPC 状态 */
  getNPCStates(): NPCState[] {
    return Array.from(this.npcs.values());
  }

  /** 按 ID 获取 NPC */
  getNPC(id: string): NPCState | undefined {
    return this.npcs.get(id);
  }

  /** 移除 NPC */
  removeNPC(id: string): void {
    const state = this.npcs.get(id);
    if (state?.mesh) {
      this.scene.remove(state.mesh);
      state.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    }
    this.npcs.delete(id);
  }

  dispose(): void {
    this.npcs.forEach((_, id) => this.removeNPC(id));
    this.materialCache.forEach((mat) => mat.dispose());
    this.materialCache.clear();
  }
}
